import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Save,
  Eye,
  Split,
  FileEdit,
  ArrowLeft,
  Send,
  Loader2,
  Settings,
  Check,
  ImagePlus,
} from 'lucide-react';
import CodeMirror, { type ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { markdown } from '@codemirror/lang-markdown';
import { oneDark } from '@codemirror/theme-one-dark';
import { useThemeStore } from '../../store/theme';
import { api } from '../../api';
import { PostDetail } from '../../types';
import { Markdown } from '../../components/Markdown';

type ViewMode = 'edit' | 'preview' | 'split';

export function EditorPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { theme } = useThemeStore();

  const isEdit = Boolean(id);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [summary, setSummary] = useState('');
  const [tags, setTags] = useState('');
  const [folder, setFolder] = useState('');
  const [contentMd, setContentMd] = useState('# 开始写作\n\n');
  const [viewMode, setViewMode] = useState<ViewMode>('split');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const [showMeta, setShowMeta] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const saveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editorRef = useRef<ReactCodeMirrorRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [initialized, setInitialized] = useState(false);

  const { data: post, isLoading } = useQuery({
    queryKey: ['admin-post', id],
    queryFn: () => api.getAdminPost(id!),
    enabled: isEdit,
  });

  const { data: draft, isLoading: draftLoading } = useQuery({
    queryKey: ['admin-draft', id],
    queryFn: () => api.getDraft(id!),
    enabled: isEdit,
  });

  // 必须等文章和草稿两个查询都返回后再初始化，
  // 否则会先用默认文本渲染，自动保存随即覆盖掉真实草稿（数据丢失）
  useEffect(() => {
    if (!initialized && !isLoading && !draftLoading && (post || draft)) {
      setTitle(post?.title || '');
      setSlug(post?.slug || '');
      setSummary(post?.summary || '');
      setTags(post?.tags.join(', ') || '');
      setFolder(post?.folder || '');
      const md = draft?.content_md || post?.content_md || '# 开始写作\n\n';
      setContentMd(md);
      setInitialized(true);
    }
  }, [post, draft, isLoading, draftLoading, initialized]);

  // 自动保存草稿
  useEffect(() => {
    if (!isEdit || !id || !initialized) return;
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => {
      handleSaveDraft();
    }, 2000);
    return () => {
      if (saveTimeout.current) clearTimeout(saveTimeout.current);
    };
  }, [contentMd, id, isEdit, initialized]);

  const createPostMutation = useMutation({
    mutationFn: () =>
      api.createPost({
        title: title || '无标题',
        slug: slug || `post-${Date.now()}`,
        summary: summary || undefined,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        folder: folder || undefined,
        content_md: contentMd,
      }),
    onSuccess: (data: PostDetail) => {
      queryClient.invalidateQueries({ queryKey: ['admin-posts'] });
      navigate(`/admin/edit/${data.id}`, { replace: true });
    },
  });

  const updatePostMutation = useMutation({
    mutationFn: () =>
      api.updatePost(id!, {
        title,
        slug,
        summary: summary || undefined,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        folder,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-post', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-posts'] });
    },
  });

  const saveDraftMutation = useMutation({
    mutationFn: () => api.saveDraft(id!, { content_md: contentMd }),
    onMutate: () => setSaving(true),
    onSuccess: () => {
      setLastSaved(new Date());
      setSaving(false);
      queryClient.setQueryData(['admin-draft', id], { content_md: contentMd });
    },
    onError: () => setSaving(false),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.uploadImage(file),
    onSuccess: ({ url }, file) => {
      // 在光标处插入图片 Markdown，没有光标就追加到末尾
      const view = editorRef.current?.view;
      const snippet = `![${file.name}](${url})`;
      if (view) {
        const pos = view.state.selection.main.head;
        view.dispatch({ changes: { from: pos, insert: snippet } });
      } else {
        setContentMd((c) => `${c}\n${snippet}\n`);
      }
    },
    onError: (e) => alert(`上传失败：${e.message}`),
  });

  const publishMutation = useMutation({
    mutationFn: () => api.publishPost(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-post', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-posts'] });
      queryClient.invalidateQueries({ queryKey: ['admin-draft', id] });
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: () => api.unpublishPost(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-post', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-posts'] });
    },
  });

  const handleSaveDraft = () => {
    if (id) saveDraftMutation.mutate();
  };

  const handlePublish = () => {
    if (!id) return;
    saveDraftMutation.mutate(undefined, {
      onSuccess: () => publishMutation.mutate(),
    });
  };

  if (isEdit && (isLoading || !initialized)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="animate-spin text-indigo-500" size={32} />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex h-screen flex-col pt-16"
    >
      {/* 顶部工具栏 */}
      <header className="flex items-center justify-between border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur dark:border-indigo-500/10 dark:bg-space-900/90">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin')}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-star-dim dark:hover:bg-space-800"
          >
            <ArrowLeft size={18} />
          </button>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="文章标题"
            className="bg-transparent text-lg font-bold text-slate-900 outline-none placeholder:text-slate-400 dark:text-star"
          />
        </div>

        <div className="flex items-center gap-2">
          <div className="mr-2 hidden items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1 dark:border-indigo-500/20 dark:bg-space-800 md:flex">
            {(['edit', 'split', 'preview'] as ViewMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                  viewMode === mode
                    ? 'bg-white text-indigo-600 shadow-sm dark:bg-space-700 dark:text-nebula-glow'
                    : 'text-slate-500 hover:text-slate-700 dark:text-star-dim dark:hover:text-star'
                }`}
              >
                {mode === 'edit' && <FileEdit size={14} className="inline mr-1" />}
                {mode === 'split' && <Split size={14} className="inline mr-1" />}
                {mode === 'preview' && <Eye size={14} className="inline mr-1" />}
                {mode === 'edit' && '编辑'}
                {mode === 'split' && '分屏'}
                {mode === 'preview' && '预览'}
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowMeta(!showMeta)}
            className={`rounded-lg border px-3 py-2 text-sm transition-colors ${
              showMeta
                ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:border-nebula dark:bg-indigo-500/10 dark:text-nebula-glow'
                : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-500 dark:border-indigo-500/20 dark:bg-space-800 dark:text-star-dim'
            }`}
          >
            <Settings size={14} className="inline mr-1" /> 元信息
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) uploadMutation.mutate(file);
              e.target.value = '';
            }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 transition-colors hover:border-indigo-500 disabled:opacity-50 dark:border-indigo-500/20 dark:bg-space-800 dark:text-star-dim"
          >
            {uploadMutation.isPending ? (
              <Loader2 size={14} className="inline animate-spin" />
            ) : (
              <ImagePlus size={14} className="inline" />
            )}
            传图
          </button>

          {!isEdit ? (
            <button
              onClick={() => createPostMutation.mutate()}
              disabled={createPostMutation.isPending}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-50 dark:bg-nebula dark:hover:bg-indigo-500"
            >
              {createPostMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              创建文章
            </button>
          ) : post?.status === 'public' ? (
            <button
              onClick={() => unpublishMutation.mutate()}
              disabled={unpublishMutation.isPending}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-indigo-500 dark:border-indigo-500/20 dark:bg-space-800 dark:text-star-dim"
            >
              转私有
            </button>
          ) : (
            <button
              onClick={handlePublish}
              disabled={publishMutation.isPending}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-50 dark:bg-nebula dark:hover:bg-indigo-500"
            >
              {publishMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              发布
            </button>
          )}
        </div>
      </header>

      {/* 元信息面板 */}
      {showMeta && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-indigo-500/10 dark:bg-space-900"
        >
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-star-dim">Slug</label>
              <input
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 dark:border-indigo-500/20 dark:bg-space-800 dark:text-star"
                placeholder="post-slug"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-star-dim">目录（用 / 分层）</label>
              <input
                value={folder}
                onChange={(e) => setFolder(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 dark:border-indigo-500/20 dark:bg-space-800 dark:text-star"
                placeholder="如 tech/rust，留空为根目录"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-star-dim">摘要</label>
              <input
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 dark:border-indigo-500/20 dark:bg-space-800 dark:text-star"
                placeholder="简短描述"
              />
            </div>
            <div className="md:col-span-3">
              <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-star-dim">标签（逗号分隔）</label>
              <input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 dark:border-indigo-500/20 dark:bg-space-800 dark:text-star"
                placeholder="rust, web, kubernetes"
              />
            </div>
          </div>
          {isEdit && (
            <button
              onClick={() => updatePostMutation.mutate()}
              className="mt-4 inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 dark:bg-nebula"
            >
              <Check size={14} /> 保存元信息
            </button>
          )}
        </motion.div>
      )}

      {/* 编辑器主体 */}
      <div className="relative flex flex-1 overflow-hidden">
        {(viewMode === 'edit' || viewMode === 'split') && (
          <div className={`flex flex-col ${viewMode === 'split' ? 'w-1/2 border-r border-slate-200 dark:border-indigo-500/20' : 'w-full'}`}>
            <CodeMirror
              ref={editorRef}
              value={contentMd}
              height="100%"
              theme={theme === 'dark' ? oneDark : 'light'}
              extensions={[markdown()]}
              onChange={(value) => setContentMd(value)}
              className="flex-1 min-h-0 text-sm"
              basicSetup={{
                lineNumbers: true,
                highlightActiveLineGutter: true,
                highlightActiveLine: true,
                foldGutter: false,
              }}
            />
          </div>
        )}

        {(viewMode === 'preview' || viewMode === 'split') && (
          <div
            ref={previewRef}
            className={`overflow-y-auto ${viewMode === 'split' ? 'w-1/2' : 'w-full'} bg-slate-50 dark:bg-space-950`}
          >
            <div className="prose prose-lg max-w-none p-8 dark:prose-invert prose-headings:scroll-mt-24"
            >
              <Markdown content={contentMd} />
            </div>
          </div>
        )}
      </div>

      {/* 状态栏 */}
      <footer className="flex items-center justify-between border-t border-slate-200 bg-white px-4 py-2 text-xs text-slate-500 dark:border-indigo-500/10 dark:bg-space-900 dark:text-star-dim">
        <div className="flex items-center gap-4">
          <span>{contentMd.length} 字符</span>
          {post && (
            <span className="rounded px-1.5 py-0.5 capitalize">
              状态：
              <span
                className={`font-medium ${
                  post.status === 'public'
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : post.status === 'private'
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-slate-600 dark:text-slate-400'
                }`}
              >
                {post.status}
              </span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {saving && <Loader2 size={12} className="animate-spin" />}
          {lastSaved && !saving && (
            <span>已保存 {lastSaved.toLocaleTimeString()}</span>
          )}
        </div>
      </footer>
    </motion.div>
  );
}
